import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Autorenew as GenerateIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  PersonAdd as PersonAddIcon,
  LockReset as LockResetIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import PageHeader from '../common/PageHeader';
import Loading from '../common/Loading';
import PortalAccountDialog from './PortalAccountDialog';
import PortalAccountEditDialog from './PortalAccountEditDialog';
import {
  clearOperatorsError,
  createCentralAccount,
  createOperator,
  createOperatorSubAccount,
  deletePortalAccount,
  fetchCentralAccounts,
  fetchOperatorUsers,
  fetchOperators,
  resetSubAccountPassword,
  setCentralAccountActive,
  setOperatorSubAccountActive,
  updateOperator,
  updatePortalAccount
} from '../../redux/slices/operatorsSlice';
import { selectCanManageOperators, changeOwnPassword, logoutUser } from '../../redux/slices/authSlice';
import { fetchSettings } from '../../redux/slices/settingsSlice';
import { accountErrorMessage } from '../../utils/accountErrors';
import {
  MIN_PASSWORD_LENGTH,
  generateInitialPassword,
  isValidEmail,
  roleLabel
} from '../../utils/portalAccounts';

const emptyOperatorForm = {
  id: '',
  name: '',
  legalName: '',
  contactName: '',
  email: '',
  phone: '',
  address: '',
  vehicleLimit: 1,
  // Quittungs-Stammdaten (§ 33 UStDV) — Pflicht für den Quittungsversand.
  taxNumber: '',
  vatId: '',
  notes: '',
  active: true,
  // Auszahlungs-Referenzdaten für App-Zahlungs-Abrechnung (Konzept 8.7).
  // Steuern KEINE Live-Zahlung — App-Zahlungen gehen immer ans Hauptkonto;
  // die Zentrale rechnet Sub-Unternehmer per Auszahlungsübersicht ab und nutzt
  // diese Angaben, um das Geld zu überweisen.
  payoutMethod: 'none', // 'none' | 'paypal' | 'bank'
  payoutPaypalEmail: '',
  payoutIban: '',
  payoutBic: '',
  payoutAccountHolder: ''
};

const OperatorsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const canManageOperators = useSelector(selectCanManageOperators);
  const currentUser = useSelector(state => state.auth.user);
  const { operators, usersByOperatorId, centralAccounts, isLoading, isSaving, error } = useSelector(state => state.operators);
  // Instanzweites Fahrzeug-Gesamtlimit (Lizenz) aus appSettings/general.
  const globalVehicleLimit = useSelector(state => state.settings?.global?.vehicleLimit ?? 10);
  // Tatsächlich angelegte Fahrzeuge (app-weit über AppDataManager geladen) —
  // dieselbe Quelle wie die Fahrzeugverwaltung.
  const vehicles = useSelector(state => state.appData?.vehicles);
  const existingVehicleCount = vehicles?.length ?? 0;

  const [operatorDialogOpen, setOperatorDialogOpen] = useState(false);
  const [operatorForm, setOperatorForm] = useState(emptyOperatorForm);
  const [expandedOperatorId, setExpandedOperatorId] = useState(null);
  // Anlage-Dialog: `variant` unterscheidet Unternehmer-Zugang und Zentral-Konto.
  const [accountDialog, setAccountDialog] = useState({ open: false, variant: 'operator', operatorId: '' });
  const [editDialogAccount, setEditDialogAccount] = useState(null);
  const [deleteDialogAccount, setDeleteDialogAccount] = useState(null);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', next: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [resetDialogUser, setResetDialogUser] = useState(null);
  const [resetForm, setResetForm] = useState({ next: '', confirm: '' });
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);
  const [resetSaving, setResetSaving] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  useEffect(() => {
    dispatch(fetchOperators());
    dispatch(fetchCentralAccounts());
    dispatch(fetchOperatorUsers());
    dispatch(fetchSettings());
  }, [dispatch]);

  const operatorCount = operators.length;
  const activeOperatorCount = useMemo(
    () => operators.filter(operator => operator.active !== false).length,
    [operators]
  );

  // Fahrzeuge je Unternehmen — dieselbe Quelle wie die Fahrzeugverwaltung, die
  // `operatorId` am Fahrzeugdokument auch für ihre Rechteprüfung nutzt.
  const vehicleCountByOperatorId = useMemo(() => {
    const counts = {};
    (vehicles || []).forEach(vehicle => {
      if (!vehicle?.operatorId) return;
      counts[vehicle.operatorId] = (counts[vehicle.operatorId] || 0) + 1;
    });
    return counts;
  }, [vehicles]);

  // Fahrzeuge der Hauptzentrale (ohne Unternehmenszuordnung).
  const centralVehicleCount = useMemo(
    () => (vehicles || []).filter(vehicle => !vehicle?.operatorId).length,
    [vehicles]
  );

  // Belegte Plätze im Lizenzrahmen. Ein vergebenes Kontingent ist eine Zusage
  // und belegt seinen Platz, auch wenn der Unternehmer noch kein Fahrzeug
  // angelegt hat. Je Unternehmen zählt `max(Kontingent, Bestand)`: Wurde ein
  // Kontingent nachträglich unter den Bestand gesenkt, belegen die vorhandenen
  // Fahrzeuge ihre Plätze weiter.
  //
  // Vorher wurde nur gegen die tatsächlich angelegten Fahrzeuge gerechnet. Ohne
  // angelegte Fahrzeuge galt damit der volle Rahmen als frei — bei 14
  // Unternehmen mit zusammen 18 von 19 Plätzen wurden dem nächsten Unternehmen
  // erneut 19 angeboten.
  const committedByOperatorId = useMemo(() => {
    const committed = {};
    operators.forEach(operator => {
      committed[operator.id] = Math.max(
        Number(operator.vehicleLimit) || 0,
        vehicleCountByOperatorId[operator.id] || 0
      );
    });
    return committed;
  }, [operators, vehicleCountByOperatorId]);

  const committedTotal = useMemo(
    () => Object.values(committedByOperatorId).reduce((sum, value) => sum + value, 0) + centralVehicleCount,
    [committedByOperatorId, centralVehicleCount]
  );

  const editingOperator = operators.find(op => op.id === operatorForm.id);
  // Beim Bearbeiten zählt das Unternehmen nicht gegen sich selbst.
  const committedByOthers = editingOperator
    ? committedTotal - (committedByOperatorId[editingOperator.id] || 0)
    : committedTotal;
  const availableForForm = Math.max(0, globalVehicleLimit - committedByOthers);
  // Bereits angelegte Fahrzeuge lassen sich nicht wegdefinieren: Das Kontingent
  // darf darunter gesetzt werden (Abbau), die Plätze bleiben aber belegt.
  const ownVehicleCount = editingOperator ? vehicleCountByOperatorId[editingOperator.id] || 0 : 0;
  const ownCommitted = editingOperator ? committedByOperatorId[editingOperator.id] || 0 : 0;
  const maxVehicleLimitForForm = availableForForm;
  const remainingVehicleQuota = Math.max(0, globalVehicleLimit - committedTotal);
  const isOvercommitted = committedTotal > globalVehicleLimit;

  const openNewOperatorDialog = () => {
    dispatch(clearOperatorsError());
    setOperatorForm(emptyOperatorForm);
    setOperatorDialogOpen(true);
  };

  const openEditOperatorDialog = (operator) => {
    dispatch(clearOperatorsError());
    setOperatorForm({
      id: operator.id,
      name: operator.name || '',
      legalName: operator.legalName || '',
      contactName: operator.contactName || '',
      email: operator.email || '',
      phone: operator.phone || '',
      address: operator.address || '',
      vehicleLimit: operator.vehicleLimit ?? 1,
      taxNumber: operator.taxNumber || '',
      vatId: operator.vatId || '',
      notes: operator.notes || '',
      active: operator.active !== false,
      // Auszahlungs-Referenzdaten sind direkt am operators-Dokument gespeichert.
      payoutMethod: operator.payoutMethod || 'none',
      payoutPaypalEmail: operator.payoutPaypalEmail || '',
      payoutIban: operator.payoutIban || '',
      payoutBic: operator.payoutBic || '',
      payoutAccountHolder: operator.payoutAccountHolder || ''
    });
    setOperatorDialogOpen(true);
  };

  const openAccountDialog = (variant, operatorId = '') => {
    dispatch(clearOperatorsError());
    setAccountDialog({ open: true, variant, operatorId });
  };

  const openEditAccountDialog = (account, isCentralAccount) => {
    dispatch(clearOperatorsError());
    setEditDialogAccount({ ...account, isCentralAccount });
  };

  // Fahrzeuglimit muss eine ganze Zahl im zulässigen Rahmen sein (keine
  // Dezimalwerte, keine leere/ungültige Eingabe).
  const vehicleLimitValue = Number(operatorForm.vehicleLimit);
  const vehicleLimitWellFormed =
    operatorForm.vehicleLimit !== '' &&
    Number.isInteger(vehicleLimitValue) &&
    vehicleLimitValue >= 1;
  // Notausgang im überbuchten Zustand: Alles, was die belegten Plätze nicht
  // erhöht, bleibt erlaubt — sonst wäre auch die reparierende Reduzierung
  // gesperrt und das Unternehmen ließe sich nicht einmal umbenennen.
  // Dieselbe Regel prüft die Cloud Function verbindlich.
  const wouldCommit = Math.max(vehicleLimitValue, ownVehicleCount);
  const vehicleLimitValid =
    vehicleLimitWellFormed &&
    (vehicleLimitValue <= maxVehicleLimitForForm ||
      (isOvercommitted && wouldCommit <= ownCommitted));

  // § 33 UStDV verlangt vom leistenden Unternehmer Name, Anschrift und
  // Steuernummer ODER USt-IdNr — eines der beiden Steuerfelder genügt, deshalb
  // trägt keines davon ein eigenes Pflichtfeld-Sternchen.
  const taxIdentifierMissing = !operatorForm.taxNumber.trim() && !operatorForm.vatId.trim();
  const operatorEmailInvalid = Boolean(operatorForm.email.trim()) && !isValidEmail(operatorForm.email);
  const operatorFormValid =
    operatorForm.name.trim() &&
    operatorForm.legalName.trim() &&
    operatorForm.address.trim() &&
    !taxIdentifierMissing &&
    !operatorEmailInvalid &&
    vehicleLimitValid;

  const handleOperatorSubmit = async () => {
    if (!operatorFormValid) return;

    // Auf eine Ganzzahl normalisieren. Nicht auf das Maximum kappen: Im
    // überbuchten Zustand ist ein Wert oberhalb des rechnerischen Maximums
    // zulässig, solange er die belegten Plätze nicht erhöht — stilles Kappen
    // würde dort das Kontingent ungefragt verändern. Die Grenze setzt
    // `vehicleLimitValid` und verbindlich die Cloud Function.
    const normalizedVehicleLimit = Math.max(1, Math.floor(Number(operatorForm.vehicleLimit)) || 1);

    // Auszahlungs-Referenzdaten werden direkt mit dem operators-Dokument
    // gespeichert (Konzept 8.7) — sie steuern keine Live-Zahlung.
    const payload = { ...operatorForm, vehicleLimit: normalizedVehicleLimit };
    const isNewOperator = !operatorForm.id;
    const action = operatorForm.id
      ? updateOperator(payload)
      : createOperator(payload);
    const result = await dispatch(action);
    if (result.error) return;

    setOperatorDialogOpen(false);
    await dispatch(fetchOperators());

    // Ein frisch angelegtes Unternehmen ohne Zugang ist nur ein halber Vorgang —
    // deshalb direkt weiter zur Zugangsanlage.
    if (isNewOperator && result.payload?.id) {
      openAccountDialog('operator', result.payload.id);
      setExpandedOperatorId(result.payload.id);
    }
  };

  const handleCreateAccount = async (form) => {
    const action = accountDialog.variant === 'operator'
      ? createOperatorSubAccount(form)
      : createCentralAccount(form);
    const result = await dispatch(action);
    if (result.error) return { ok: false };
    if (accountDialog.variant === 'operator' && form.operatorId) {
      setExpandedOperatorId(form.operatorId);
    }
    return { ok: true, email: result.payload?.email };
  };

  const handleEditAccountSave = async (values) => {
    const result = await dispatch(updatePortalAccount({
      userId: editDialogAccount.id,
      isCentralAccount: editDialogAccount.isCentralAccount,
      ...values
    }));
    if (result.error) return;
    setEditDialogAccount(null);
  };

  const handleDeleteAccount = async () => {
    const result = await dispatch(deletePortalAccount({
      userId: deleteDialogAccount.id,
      isCentralAccount: deleteDialogAccount.isCentralAccount
    }));
    if (result.error) return;
    setDeleteDialogAccount(null);
  };

  const openPasswordDialog = () => {
    setPasswordForm({ current: '', next: '', confirm: '' });
    setPasswordError('');
    setPasswordDialogOpen(true);
  };

  const handlePasswordSubmit = async () => {
    setPasswordError('');
    if (passwordForm.next.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`Das neue Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }
    if (passwordForm.next !== passwordForm.confirm) {
      setPasswordError('Die Passwort-Bestätigung stimmt nicht überein.');
      return;
    }
    setPasswordSaving(true);
    const result = await dispatch(changeOwnPassword({
      currentPassword: passwordForm.current,
      newPassword: passwordForm.next
    }));
    setPasswordSaving(false);
    if (changeOwnPassword.rejected.match(result)) {
      setPasswordError(accountErrorMessage(result.payload, 'Passwort konnte nicht geändert werden.'));
      return;
    }
    // Erfolg → automatische Abmeldung und zurück zum Login.
    setPasswordDialogOpen(false);
    await dispatch(logoutUser());
    navigate('/login', { replace: true });
  };

  const openResetDialog = (user) => {
    setResetDialogUser(user);
    setResetForm({ next: '', confirm: '' });
    setResetError('');
    setResetSuccess(false);
    setShowResetPassword(false);
  };

  const handleResetSubmit = async () => {
    setResetError('');
    if (resetForm.next.length < MIN_PASSWORD_LENGTH) {
      setResetError(`Das neue Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`);
      return;
    }
    if (resetForm.next !== resetForm.confirm) {
      setResetError('Die Passwort-Bestätigung stimmt nicht überein.');
      return;
    }
    setResetSaving(true);
    const result = await dispatch(resetSubAccountPassword({
      userId: resetDialogUser.id,
      newPassword: resetForm.next
    }));
    setResetSaving(false);
    if (resetSubAccountPassword.rejected.match(result)) {
      setResetError(accountErrorMessage(result.payload, 'Passwort konnte nicht zurückgesetzt werden.'));
      return;
    }
    setResetSuccess(true);
  };

  const toggleCentralAccountActive = (user) => {
    dispatch(setCentralAccountActive({
      userId: user.id,
      active: user.active === false
    }));
  };

  const toggleSubAccountActive = (user) => {
    dispatch(setOperatorSubAccountActive({
      userId: user.id,
      active: user.active === false
    }));
  };

  if (!canManageOperators) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Sie haben keine Berechtigung für die Verwaltung von Unternehmen und Portalzugängen.
        </Alert>
      </Box>
    );
  }

  if (isLoading && operators.length === 0) {
    return <Loading message="Unternehmen werden geladen..." />;
  }

  const statusChip = (account) => (
    <Chip
      size="small"
      label={account.active === false ? 'Inaktiv' : 'Aktiv'}
      color={account.active === false ? 'default' : 'success'}
    />
  );

  // Aktionen sind für Zentral-Konten und Unternehmer-Zugänge identisch — nur die
  // Rückfrage beim Löschen unterscheidet sich im Text.
  const accountActions = (account, isCentralAccount) => {
    const isSelf = account.id === currentUser?.uid;
    const isActive = account.active !== false;
    return (
      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
        <Tooltip title="Stammdaten bearbeiten">
          <span>
            <IconButton
              size="small"
              onClick={() => openEditAccountDialog(account, isCentralAccount)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Passwort zurücksetzen">
          <span>
            <IconButton size="small" disabled={isSelf} onClick={() => openResetDialog(account)}>
              <LockResetIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Button
          size="small"
          disabled={isActive && isSelf}
          onClick={() => (isCentralAccount ? toggleCentralAccountActive(account) : toggleSubAccountActive(account))}
        >
          {isActive ? 'Deaktivieren' : 'Aktivieren'}
        </Button>
        <Tooltip title={isActive ? 'Zum Löschen zuerst deaktivieren' : 'Zugang endgültig löschen'}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={isActive || isSelf}
              onClick={() => setDeleteDialogAccount({ ...account, isCentralAccount })}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <PageHeader
        title="Unternehmen & Portalzugänge"
        description="Unternehmen, Kontingente und Portal-Zugänge verwalten"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Profil', path: '/profil' },
          { label: 'Unternehmen', path: '/profil/unternehmer' }
        ]}
        actionButton={{
          label: 'Unternehmen anlegen',
          icon: <AddIcon />,
          onClick: openNewOperatorDialog
        }}
        secondaryButtons={[
          {
            label: 'Zentral-Konto anlegen',
            icon: <AdminIcon />,
            onClick: () => openAccountDialog('central')
          },
          {
            label: 'Unternehmer-Zugang anlegen',
            icon: <PersonAddIcon />,
            onClick: () => openAccountDialog('operator')
          },
          {
            label: 'Mein Passwort ändern',
            icon: <LockResetIcon />,
            onClick: openPasswordDialog
          }
        ]}
      />

      {/* Fehler außerhalb der Dialoge — die Dialoge zeigen ihren Fehler selbst,
          sonst bliebe er hinter dem Modal unsichtbar. */}
      {error && !operatorDialogOpen && !accountDialog.open && !editDialogAccount && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => dispatch(clearOperatorsError())}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip label={`${activeOperatorCount}/${operatorCount} Unternehmen aktiv`} color="primary" />
        <Tooltip title="Tatsächlich angelegte Fahrzeuge über alle Unternehmen und die Zentrale.">
          <Chip
            label={`Fahrzeuge angelegt: ${existingVehicleCount}`}
            variant="outlined"
          />
        </Tooltip>
        <Tooltip
          title={
            isOvercommitted
              ? 'Die belegten Plätze übersteigen den Lizenzrahmen. Das kann nur durch bereits angelegte Fahrzeuge entstehen, deren Kontingent nachträglich gesenkt wurde.'
              : 'Belegte Plätze im Lizenzrahmen: vergebene Kontingente (mindestens die tatsächlich angelegten Fahrzeuge) plus Fahrzeuge der Zentrale.'
          }
        >
          <Chip
            label={`Lizenzrahmen belegt: ${committedTotal}/${globalVehicleLimit}`}
            variant="outlined"
            color={isOvercommitted ? 'error' : remainingVehicleQuota === 0 ? 'warning' : 'default'}
          />
        </Tooltip>
        <Chip
          label={`Noch vergebbar: ${remainingVehicleQuota}`}
          variant="outlined"
          color={remainingVehicleQuota === 0 ? 'warning' : 'success'}
        />
        <Chip label="Portalzugänge haben keinen Zugriff auf die Fahrer-App" variant="outlined" />
      </Stack>

      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          Hauptzentrale
        </Typography>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Rolle</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {centralAccounts.map(user => (
                <TableRow key={user.id}>
                  <TableCell>
                    <Typography variant="subtitle2">{user.name || user.email}</Typography>
                    {user.id === currentUser?.uid && (
                      <Typography variant="caption" color="text.secondary">Ihr Konto</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>{user.email || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{user.phone || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={roleLabel(user.role)}
                      color={user.role === 'admin' || user.role === 'central_admin' ? 'primary' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{statusChip(user)}</TableCell>
                  <TableCell align="right">{accountActions(user, true)}</TableCell>
                </TableRow>
              ))}
              {centralAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="text.secondary">Noch keine Konten der Hauptzentrale geladen.</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Unternehmen
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} />
              <TableCell>Unternehmen</TableCell>
              <TableCell>Kontakt</TableCell>
              <TableCell align="center">Fahrzeuge</TableCell>
              <TableCell>Portalzugänge</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {operators.map(operator => {
              const users = usersByOperatorId[operator.id] || [];
              const activeUserCount = users.filter(user => user.active !== false).length;
              const isExpanded = expandedOperatorId === operator.id;
              const vehicleCount = vehicleCountByOperatorId[operator.id] || 0;
              const vehicleLimit = operator.vehicleLimit ?? 0;

              return (
                <React.Fragment key={operator.id}>
                  <TableRow>
                    <TableCell>
                      <Tooltip title={isExpanded ? 'Zugänge ausblenden' : 'Zugänge anzeigen'}>
                        <span>
                          <IconButton
                            size="small"
                            disabled={users.length === 0}
                            onClick={() => setExpandedOperatorId(isExpanded ? null : operator.id)}
                          >
                            {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">{operator.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {operator.legalName || operator.id}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>{statusChip(operator)}</Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{operator.contactName || '-'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {operator.email || operator.phone || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        color={vehicleCount > vehicleLimit ? 'warning.main' : 'text.primary'}
                      >
                        {vehicleCount} / {vehicleLimit}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {vehicleCount > vehicleLimit
                          ? `angelegt / Kontingent — belegt ${vehicleCount}`
                          : 'angelegt / Kontingent'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {users.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">Keine Zugänge</Typography>
                      ) : (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${activeUserCount}/${users.length} aktiv`}
                          onClick={() => setExpandedOperatorId(isExpanded ? null : operator.id)}
                        />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" startIcon={<EditIcon />} onClick={() => openEditOperatorDialog(operator)}>
                          Bearbeiten
                        </Button>
                        <Button
                          size="small"
                          startIcon={<PersonAddIcon />}
                          onClick={() => openAccountDialog('operator', operator.id)}
                        >
                          Zugang
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ py: 0, borderBottom: isExpanded ? undefined : 'none' }} colSpan={6}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ my: 2, ml: 6 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                            Portalzugänge von {operator.name}
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>E-Mail</TableCell>
                                <TableCell>Telefon</TableCell>
                                <TableCell>Rolle</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Aktionen</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {users.map(user => (
                                <TableRow key={user.id}>
                                  <TableCell>{user.name || '-'}</TableCell>
                                  <TableCell sx={{ wordBreak: 'break-all' }}>{user.email || '-'}</TableCell>
                                  <TableCell>{user.phone || '-'}</TableCell>
                                  <TableCell>
                                    <Chip size="small" label={roleLabel(user.role)} />
                                  </TableCell>
                                  <TableCell>{statusChip(user)}</TableCell>
                                  <TableCell align="right">{accountActions(user, false)}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {operators.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography color="text.secondary">Noch keine Unternehmen angelegt.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={operatorDialogOpen} onClose={() => setOperatorDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{operatorForm.id ? 'Unternehmen bearbeiten' : 'Unternehmen anlegen'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Ohne freies Kontingent lässt sich kein Unternehmen anlegen — das
                gehört an den Anfang, nicht erst ans Feld weiter unten. */}
            {!operatorForm.id && maxVehicleLimitForForm <= 0 && (
              <Alert severity="warning">
                Der Lizenzrahmen von {globalVehicleLimit} Fahrzeugen ist vollständig
                vergeben ({committedTotal} Plätze belegt). Für ein weiteres Unternehmen
                muss zuerst das Kontingent eines bestehenden Unternehmens reduziert
                oder die Lizenz erweitert werden.
              </Alert>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Stammdaten
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  required
                  label="Anzeigename"
                  value={operatorForm.name}
                  onChange={(e) => setOperatorForm({ ...operatorForm, name: e.target.value })}
                  helperText="Dieser Name wird in Listen, Zuordnungen und Zugängen angezeigt."
                />
                <TextField
                  fullWidth
                  required
                  label="Rechtlicher Name"
                  value={operatorForm.legalName}
                  onChange={(e) => setOperatorForm({ ...operatorForm, legalName: e.target.value })}
                  placeholder="Vollständige Firmierung, z. B. Muster Taxi GmbH"
                  helperText="Erscheint als leistender Unternehmer auf den Quittungen (§ 33 UStDV)."
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Kontakt
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  label="Ansprechpartner"
                  value={operatorForm.contactName}
                  onChange={(e) => setOperatorForm({ ...operatorForm, contactName: e.target.value })}
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      type="email"
                      label="E-Mail"
                      value={operatorForm.email}
                      onChange={(e) => setOperatorForm({ ...operatorForm, email: e.target.value })}
                      error={operatorEmailInvalid}
                      helperText={operatorEmailInvalid ? 'Die E-Mail-Adresse hat kein gültiges Format.' : ' '}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Telefon"
                      value={operatorForm.phone}
                      onChange={(e) => setOperatorForm({ ...operatorForm, phone: e.target.value })}
                    />
                  </Grid>
                </Grid>
                <TextField
                  fullWidth
                  required
                  label="Adresse"
                  value={operatorForm.address}
                  onChange={(e) => setOperatorForm({ ...operatorForm, address: e.target.value })}
                  placeholder="Straße Hausnr., PLZ Ort"
                  helperText="Anschrift des Unternehmens — Pflicht für Quittungen."
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Steuerliche Angaben (für Quittungen)
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                Diese Angaben erscheinen auf den automatisch erstellten Quittungen
                der Fahrten dieses Unternehmens. Eine vorläufige Steuernummer kann
                später geändert werden; neue Quittungen verwenden dann automatisch
                die aktuelle Nummer.
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Steuernummer"
                    value={operatorForm.taxNumber}
                    onChange={(e) => setOperatorForm({ ...operatorForm, taxNumber: e.target.value })}
                    placeholder="z. B. 12/345/67890"
                    error={taxIdentifierMissing}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="USt-IdNr"
                    value={operatorForm.vatId}
                    onChange={(e) => setOperatorForm({ ...operatorForm, vatId: e.target.value })}
                    placeholder="z. B. DE123456789"
                    error={taxIdentifierMissing}
                  />
                </Grid>
              </Grid>
              {taxIdentifierMissing && (
                <Typography variant="caption" color="error" component="div" sx={{ mt: 0.5 }}>
                  Bitte Steuernummer oder USt-IdNr angeben — eines von beiden ist
                  für die Quittungen Pflicht (§ 33 UStDV).
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Kontingent
              </Typography>
              <TextField
                fullWidth
                required
                type="number"
                label="Fahrzeuglimit"
                value={operatorForm.vehicleLimit}
                onChange={(e) => setOperatorForm({ ...operatorForm, vehicleLimit: e.target.value })}
                inputProps={{ min: 1, max: maxVehicleLimitForForm, step: 1 }}
                error={!vehicleLimitValid}
                helperText={
                  isOvercommitted
                    ? `Der Lizenzrahmen ist überschritten (${committedTotal}/${globalVehicleLimit} belegt). Möglich sind derzeit nur Werte, die die belegten Plätze nicht erhöhen.`
                    : !vehicleLimitWellFormed
                      ? 'Bitte eine ganze Zahl ab 1 eingeben.'
                      : !vehicleLimitValid
                        ? `Höchstens ${maxVehicleLimitForForm} möglich — ${committedByOthers} von ${globalVehicleLimit} Plätzen sind bereits vergeben oder belegt.`
                        : `Höchstens ${maxVehicleLimitForForm} möglich — ${committedByOthers} von ${globalVehicleLimit} Plätzen sind an andere Unternehmen vergeben oder in der Zentrale belegt.`
                }
              />

              {/* Kontingent unter den Bestand gesenkt: die vorhandenen Fahrzeuge
                  bleiben nutzbar, nur neue lassen sich nicht mehr anlegen. */}
              {editingOperator && vehicleLimitValid && vehicleLimitValue < ownVehicleCount && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Dieses Unternehmen hat bereits <b>{ownVehicleCount} Fahrzeuge</b> angelegt.
                  Mit einem Kontingent von {vehicleLimitValue} bleiben diese Fahrzeuge
                  bestehen und weiter nutzbar — der Unternehmer kann nur so lange
                  kein neues mehr anlegen, bis er unter das Kontingent kommt.
                  Für den Lizenzrahmen zählen weiterhin die {ownVehicleCount} vorhandenen
                  Fahrzeuge, das Senken gibt also keine Plätze frei.
                </Alert>
              )}

              {isOvercommitted && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  Im Lizenzrahmen von {globalVehicleLimit} Fahrzeugen sind derzeit
                  {' '}{committedTotal} Plätze belegt. Das kann nur durch bereits
                  angelegte Fahrzeuge entstehen, deren Kontingent nachträglich
                  gesenkt wurde — bitte Fahrzeuge abbauen oder die Lizenz erweitern.
                </Alert>
              )}
            </Box>

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Auszahlung (App-Zahlungen)
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1.5 }}>
                App-Zahlungen laufen immer über das Hauptkonto der Zentrale.
                Diese Angaben sind optional und dienen nur der Abrechnung: Sie
                hinterlegen, wie der Unternehmer seinen Anteil aus der
                Auszahlungsübersicht erhalten möchte.
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                <InputLabel>Auszahlungsart</InputLabel>
                <Select
                  label="Auszahlungsart"
                  value={operatorForm.payoutMethod}
                  onChange={(e) => setOperatorForm({ ...operatorForm, payoutMethod: e.target.value })}
                >
                  <MenuItem value="none">Keine Angabe</MenuItem>
                  <MenuItem value="paypal">PayPal</MenuItem>
                  <MenuItem value="bank">Banküberweisung</MenuItem>
                </Select>
              </FormControl>

              {operatorForm.payoutMethod === 'paypal' && (
                <TextField
                  fullWidth
                  size="small"
                  type="email"
                  label="PayPal-E-Mail-Adresse"
                  placeholder="zahlungen@sub-unternehmen.de"
                  value={operatorForm.payoutPaypalEmail}
                  onChange={(e) => setOperatorForm({ ...operatorForm, payoutPaypalEmail: e.target.value })}
                />
              )}

              {operatorForm.payoutMethod === 'bank' && (
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Kontoinhaber"
                    value={operatorForm.payoutAccountHolder}
                    onChange={(e) => setOperatorForm({ ...operatorForm, payoutAccountHolder: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="IBAN"
                    value={operatorForm.payoutIban}
                    onChange={(e) => setOperatorForm({ ...operatorForm, payoutIban: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="BIC (optional)"
                    value={operatorForm.payoutBic}
                    onChange={(e) => setOperatorForm({ ...operatorForm, payoutBic: e.target.value })}
                  />
                </Stack>
              )}
            </Box>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notizen"
              value={operatorForm.notes}
              onChange={(e) => setOperatorForm({ ...operatorForm, notes: e.target.value })}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={operatorForm.active}
                  onChange={(e) => setOperatorForm({ ...operatorForm, active: e.target.checked })}
                />
              }
              label="Unternehmen ist aktiv"
            />
            {!operatorForm.active && operatorForm.id && (
              <Alert severity="info">
                Ein inaktives Unternehmen sperrt zugleich die Anmeldung aller
                zugehörigen Portalzugänge.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOperatorDialogOpen(false)}>Abbrechen</Button>
          <Button variant="contained" disabled={isSaving || !operatorFormValid} onClick={handleOperatorSubmit}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <PortalAccountDialog
        open={accountDialog.open}
        variant={accountDialog.variant}
        operators={operators}
        initialOperatorId={accountDialog.operatorId}
        isSaving={isSaving}
        error={error}
        onCreate={handleCreateAccount}
        onClose={() => setAccountDialog(prev => ({ ...prev, open: false }))}
      />

      <PortalAccountEditDialog
        open={Boolean(editDialogAccount)}
        account={editDialogAccount}
        isCentralAccount={editDialogAccount?.isCentralAccount}
        isSaving={isSaving}
        error={error}
        onSave={handleEditAccountSave}
        onClose={() => setEditDialogAccount(null)}
      />

      <Dialog open={Boolean(deleteDialogAccount)} onClose={() => setDeleteDialogAccount(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Zugang löschen?</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error && <Alert severity="error">{error}</Alert>}
            <Alert severity="warning">
              <b>{deleteDialogAccount?.name || deleteDialogAccount?.email}</b> wird
              endgültig entfernt — Portalzugang und Anmeldekonto. Das lässt sich
              nicht rückgängig machen; bereits erfasste Fahrten und Belege bleiben
              unverändert erhalten.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogAccount(null)}>Abbrechen</Button>
          <Button color="error" variant="contained" disabled={isSaving} onClick={handleDeleteAccount}>
            Endgültig löschen
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={passwordDialogOpen} onClose={() => setPasswordDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Mein Passwort ändern</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Alert severity="info">
              Ändert das Passwort Ihres aktuell angemeldeten Kontos
              ({currentUser?.email || 'unbekannt'}). Nach dem Ändern werden Sie
              automatisch abgemeldet und müssen sich neu anmelden.
            </Alert>
            {passwordError && <Alert severity="error">{passwordError}</Alert>}
            <TextField
              type="password"
              label="Aktuelles Passwort"
              value={passwordForm.current}
              onChange={(e) => setPasswordForm({ ...passwordForm, current: e.target.value })}
              autoComplete="current-password"
            />
            <TextField
              type="password"
              label="Neues Passwort"
              value={passwordForm.next}
              onChange={(e) => setPasswordForm({ ...passwordForm, next: e.target.value })}
              helperText={`Mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`}
              autoComplete="new-password"
            />
            <TextField
              type="password"
              label="Neues Passwort bestätigen"
              value={passwordForm.confirm}
              onChange={(e) => setPasswordForm({ ...passwordForm, confirm: e.target.value })}
              autoComplete="new-password"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialogOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={
              passwordSaving ||
              !passwordForm.current ||
              passwordForm.next.length < MIN_PASSWORD_LENGTH ||
              passwordForm.next !== passwordForm.confirm
            }
            onClick={handlePasswordSubmit}
          >
            Ändern & abmelden
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetDialogUser)} onClose={() => setResetDialogUser(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Passwort zurücksetzen</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {resetSuccess ? (
              <>
                <Alert severity="success">
                  Das Passwort für {resetDialogUser?.name || resetDialogUser?.email} wurde
                  gesetzt. Bitte teilen Sie es dem Zugang sicher mit — der Nutzer muss sich
                  anschließend neu anmelden.
                </Alert>
                {/* Das gesetzte Passwort noch einmal zum Weitergeben — nach dem
                    Schließen ist es nicht mehr abrufbar, nur neu setzbar. */}
                <Box sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ minWidth: 90, color: 'text.secondary' }}>E-Mail</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                        {resetDialogUser?.email || '-'}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" sx={{ minWidth: 90, color: 'text.secondary' }}>Passwort</Typography>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', flexGrow: 1 }}>
                        {showResetPassword ? resetForm.next : '•'.repeat(resetForm.next.length)}
                      </Typography>
                      <Tooltip title={showResetPassword ? 'Verbergen' : 'Anzeigen'}>
                        <IconButton size="small" onClick={() => setShowResetPassword(prev => !prev)}>
                          {showResetPassword
                            ? <VisibilityOffIcon fontSize="small" />
                            : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              </>
            ) : (
              <>
                <Alert severity="info">
                  Setzt das Portal-Passwort für <b>{resetDialogUser?.name || resetDialogUser?.email}</b>
                  {resetDialogUser?.email ? ` (${resetDialogUser.email})` : ''}.
                  Das Passwort wird nicht gespeichert und muss dem Zugang sicher mitgeteilt werden.
                </Alert>
                {resetError && <Alert severity="error">{resetError}</Alert>}
                <TextField
                  type={showResetPassword ? 'text' : 'password'}
                  label="Neues Passwort"
                  value={resetForm.next}
                  onChange={(e) => setResetForm({ ...resetForm, next: e.target.value })}
                  helperText={`Mindestens ${MIN_PASSWORD_LENGTH} Zeichen.`}
                  autoComplete="new-password"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title={showResetPassword ? 'Verbergen' : 'Anzeigen'}>
                          <IconButton size="small" onClick={() => setShowResetPassword(prev => !prev)}>
                            {showResetPassword
                              ? <VisibilityOffIcon fontSize="small" />
                              : <VisibilityIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Passwort vorschlagen">
                          <IconButton
                            size="small"
                            onClick={() => {
                              const suggestion = generateInitialPassword();
                              setResetForm({ next: suggestion, confirm: suggestion });
                              setShowResetPassword(true);
                            }}
                          >
                            <GenerateIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />
                <TextField
                  type={showResetPassword ? 'text' : 'password'}
                  label="Neues Passwort bestätigen"
                  value={resetForm.confirm}
                  onChange={(e) => setResetForm({ ...resetForm, confirm: e.target.value })}
                  autoComplete="new-password"
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogUser(null)}>{resetSuccess ? 'Schließen' : 'Abbrechen'}</Button>
          {!resetSuccess && (
            <Button
              variant="contained"
              disabled={
                resetSaving ||
                resetForm.next.length < MIN_PASSWORD_LENGTH ||
                resetForm.next !== resetForm.confirm
              }
              onClick={handleResetSubmit}
            >
              Passwort setzen
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OperatorsPage;
